import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * CMS live-preview bridge (DIT-264). Active ONLY when the page is loaded with
 * `?cms_preview=1` (the admin embeds it in an iframe). It listens for draft
 * content-block values posted by the admin and overlays them onto this locale's
 * i18next instance at runtime — because every block is read through `t()`, one
 * `addResourceBundle` + a re-render makes the whole page reflect the draft
 * without touching any render site. It also highlights the element a given slug
 * renders into, located via an invisible sentinel injected into its value.
 *
 * Inert for normal visitors (the effect early-returns when the flag is absent);
 * overlays are client-side only and never persisted.
 */

const ALLOWED_ORIGINS = [
  "https://admin.stage.done-it.app",
  "https://admin.done-it.app",
  "http://localhost:5173",
];

// Two INVISIBLE SEPARATOR chars — imperceptible in the rendered text, but
// unique enough to locate the highlighted block's text node.
const SENTINEL = "⁣⁣";
const HIGHLIGHT_STYLE = "2px solid #6C5CE7";

const isPreview = () =>
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("cms_preview");

// "ns.rest.of.key" → ["ns", "rest.of.key"] (flat keys after the first dot).
function splitSlug(slug: string): [string, string] | null {
  const i = slug.indexOf(".");
  return i < 0 ? null : [slug.slice(0, i), slug.slice(i + 1)];
}

interface PreviewMessage {
  type: "cms-preview";
  blocks: Record<string, string>;
  highlight?: string;
}

export function CmsPreviewBridge() {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!isPreview()) return;
    let highlighted: HTMLElement | null = null;

    const clear = () => {
      if (highlighted) {
        highlighted.style.outline = "";
        highlighted.style.outlineOffset = "";
        highlighted = null;
      }
    };

    const locateAndHighlight = () => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let node: Node | null;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.includes(SENTINEL)) {
          const el = node.parentElement;
          if (el) {
            clear();
            el.style.outline = HIGHLIGHT_STYLE;
            el.style.outlineOffset = "2px";
            el.scrollIntoView({ block: "center", behavior: "smooth" });
            highlighted = el;
          }
          return;
        }
      }
      clear(); // selected block not on this page
    };

    const onMessage = (e: MessageEvent) => {
      if (!ALLOWED_ORIGINS.includes(e.origin)) return;
      const data = e.data as PreviewMessage;
      if (!data || data.type !== "cms-preview") return;
      const lng = i18n.language;
      for (const [slug, value] of Object.entries(data.blocks ?? {})) {
        const parts = splitSlug(slug);
        if (!parts) continue;
        const [ns, key] = parts;
        const v = slug === data.highlight ? SENTINEL + (value ?? "") : (value ?? "");
        i18n.addResourceBundle(lng, ns, { [key]: v }, true, true);
      }
      i18n.emit("languageChanged", lng); // re-render every t() consumer
      // Highlight after React has painted the new text.
      requestAnimationFrame(() => requestAnimationFrame(locateAndHighlight));
    };

    window.addEventListener("message", onMessage);
    // Tell the embedding admin we're ready to receive the current draft.
    window.parent?.postMessage({ type: "cms-preview-ready" }, "*");
    return () => {
      window.removeEventListener("message", onMessage);
      clear();
    };
  }, [i18n]);

  return null;
}
